import { ReactNode, createContext, useContext } from 'react'
import { IVenueLayoutContext } from 'types/ui'
import { Venue, VenueEvent } from 'types/model'

import { Footer } from '@components/footer'
import { useVisibilityObserver } from '@hooks/intersection-observer'
import { VenuePageBanner } from '../views/venue-page-banner'
import { VenuePageHeader } from '../views/venue-page-header'
import { EventBanner } from '@modules/event/views/event-banner'
import classNames from 'classnames'
import { FacebookPixelScript } from '@components/facebook-pixel-script'

export function VenuePageLayout({
  children,
  venue,
  facebookPixelId,
  event,
  eventPoster,
  largeBanner = false,
  minimalLayout = false,
}: {
  children: ReactNode
  venue: Venue
  facebookPixelId?: string | null
  event?: VenueEvent | null
  eventPoster?: boolean
  largeBanner?: boolean
  minimalLayout?: boolean
}) {
  const bannerObserver = useVisibilityObserver({
    threshold: 0.1,
    initiallyVisible: true,
  })

  return (
    <VenuePageLayoutContext bannerVisible={bannerObserver.visible}>
      <div
        id='venue-page-main'
        className={classNames(
          'w-screen overflow-y-auto overflow-x-hidden h-dscreen',
          {
            'pt-[36px] sm:pt-[40px]': !minimalLayout,
          }
        )}
      >
        {!minimalLayout && (
          <>
            {/* Header */}
            <VenuePageHeader
              venue={venue}
              transparent={bannerObserver.visible}
              showLogo={!bannerObserver.visible}
            />
            {/* Banner */}
            <div className='-mt-[36px] sm:-mt-[40px]'>
              {eventPoster && event ? (
                <EventBanner
                  venue={venue}
                  event={event}
                  ref={bannerObserver.observe}
                />
              ) : (
                <VenuePageBanner
                  venue={venue}
                  ref={bannerObserver.observe}
                  large={largeBanner}
                />
              )}
            </div>
          </>
        )}
        {/* Contents */}
        <div>{children}</div>
        {!minimalLayout && <Footer />}
      </div>
      <FacebookPixelScript pixel={facebookPixelId ?? venue.trackingFacebookPixelId} />
    </VenuePageLayoutContext>
  )
}

const venueLayoutContext = createContext<IVenueLayoutContext>(
  {} as IVenueLayoutContext
)

function VenuePageLayoutContext({
  bannerVisible,
  children,
}: IVenueLayoutContext & { children: ReactNode }) {
  return (
    <venueLayoutContext.Provider value={{ bannerVisible }}>
      {children}
    </venueLayoutContext.Provider>
  )
}

export function useVenuePageLayoutContext() {
  return useContext(venueLayoutContext)
}
